import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RefreshCw, Database, ChevronDown, FileUp } from "lucide-react";
import { toast } from "sonner";

// Tally → ERP sync (backend/tally_sync.py). The Tally Bridge Agent on the
// office Tally PC pulls masters (parties, stock, ledgers, groups, units …) and
// vouchers (sales, receipts, purchases, payments, journals, notes …) out of
// Tally; this page configures that and shows what came in.

const inr = (n) => (Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const when = (s) => (s ? new Date(s).toLocaleString() : "—");
const list = (a) => (a || []).join(", ");
const unlist = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

// What the agent pulls from Tally (tally_sync.PULL_KINDS).
const PULL_OPTIONS = [
  { value: "sales_order", label: "Sales Invoice" },
  { value: "stock", label: "Stock" },
  { value: "purchase", label: "Purchase" },
  { value: "ledger", label: "Ledger" },
  { value: "payment", label: "Payment" },
  { value: "journal", label: "Journal" },
  { value: "contra", label: "Contra" },
  { value: "credit_note", label: "Credit Note" },
  { value: "debit_note", label: "Debit Note" },
  { value: "masters", label: "All masters" },
];
// Voucher kind → tab label and the config field holding its Tally type names.
const VOUCHER_KINDS = [
  { kind: "receipt", label: "Receipts", field: "receipt_voucher_types", typeLabel: "Receipt" },
  { kind: "sales_order", label: "Sales Invoices", field: "sales_order_voucher_types", typeLabel: "Sales / Sales Order" },
  { kind: "purchase", label: "Purchases", field: "purchase_voucher_types", typeLabel: "Purchase" },
  { kind: "purchase_order", label: "Purchase Orders", field: "purchase_order_voucher_types", typeLabel: "Purchase Order" },
  { kind: "payment", label: "Payments", field: "payment_voucher_types", typeLabel: "Payment" },
  { kind: "journal", label: "Journals", field: "journal_voucher_types", typeLabel: "Journal" },
  { kind: "contra", label: "Contra", field: "contra_voucher_types", typeLabel: "Contra" },
  { kind: "credit_note", label: "Credit Notes", field: "credit_note_voucher_types", typeLabel: "Credit Note" },
  { kind: "debit_note", label: "Debit Notes", field: "debit_note_voucher_types", typeLabel: "Debit Note" },
];
const VOUCHER_TABS = VOUCHER_KINDS.map((v) => v.kind);
// tally_sync.MASTER_TYPES
const MASTER_TYPES = [
  { value: "ledger", label: "Ledgers" },
  { value: "group", label: "Groups" },
  { value: "stock_group", label: "Stock Groups" },
  { value: "unit", label: "Units" },
  { value: "godown", label: "Godowns" },
  { value: "cost_centre", label: "Cost Centres" },
  { value: "voucher_type", label: "Voucher Types" },
];
const masterLabel = (t) => MASTER_TYPES.find((m) => m.value === t)?.label || t;
// Tally balances: debit is negative.
const drcr = (n) => (n == null ? "—" : `${inr(Math.abs(n))} ${n < 0 ? "Dr" : n > 0 ? "Cr" : ""}`.trim());

function Table({ cols, rows, empty }) {
  if (!rows.length) return <div className="text-sm text-slate-500 py-6 text-center">{empty}</div>;
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-md">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs">
          <tr>{cols.map((c) => <th key={c.label} className={`px-3 py-2 ${c.right ? "text-right" : "text-left"}`}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || r.guid || i} className="border-t border-slate-100 align-top">
              {cols.map((c) => <td key={c.label} className={`px-3 py-2 ${c.right ? "text-right tabular-nums" : ""}`}>{c.render(r)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function TallyData() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [cfg, setCfg] = useState(null);
  const [runs, setRuns] = useState([]);
  const [tab, setTab] = useState("receipt");
  const [vouchers, setVouchers] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [imported, setImported] = useState(null);
  const [stock, setStock] = useState({ total: 0, items: [] });
  const [masterType, setMasterType] = useState("ledger");
  const [masters, setMasters] = useState({ total: 0, counts: {}, items: [] });

  const loadStatus = async () => {
    try {
      const [s, r] = await Promise.all([api.get("/tally-sync/settings"), api.get("/tally-sync/runs", { params: { limit: 15 } })]);
      setCfg(s.data);
      setRuns(r.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not load Tally sync"); }
  };

  const loadTab = async (t = tab, mt = masterType) => {
    try {
      if (VOUCHER_TABS.includes(t)) {
        const { data } = await api.get("/tally-sync/vouchers", { params: { kind: t, q } });
        setVouchers(data || []);
      } else if (t === "stock_items") {
        const { data } = await api.get("/tally-sync/stock-items", { params: { q } });
        setStock(data || { total: 0, items: [] });
      } else if (t === "masters") {
        const { data } = await api.get("/tally-sync/masters", { params: { type: mt, q } });
        setMasters(data || { total: 0, counts: {}, items: [] });
      }
    } catch { /* ignore */ }
  };

  useEffect(() => { loadStatus(); }, []);
  useEffect(() => { loadTab(tab); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true);
    try {
      const { masters_at, voucher_cursors, ...payload } = cfg;
      await api.put("/tally-sync/settings", payload);
      toast.success("Tally sync settings saved");
      await loadStatus();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const importXml = async () => {
    if (!importFile) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post("/tally-sync/import-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImported(data);
      toast.success("Tally file imported");
      await Promise.all([loadStatus(), loadTab(tab)]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Import failed"); }
    finally { setBusy(false); }
  };

  const post = async (path, msg) => {
    try { await api.post(path); toast.success(msg); await loadStatus(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  if (!cfg) return <div className="text-slate-500 text-sm">Loading...</div>;

  const voucherCols = [
    { label: "Date", render: (v) => v.date || "—" },
    { label: "No.", render: (v) => v.number || "—" },
    { label: "Party", render: (v) => v.party || "—" },
    { label: ["receipt", "payment", "journal", "contra"].includes(tab) ? "Against" : "Items", render: (v) => ["receipt", "payment", "journal", "contra"].includes(tab)
      ? (v.bills || []).map((b) => `${b.name} (₹${inr(b.amount)})`).join(", ") || "On account"
      : (v.inventory || []).map((l) => `${l.item_code || l.item} × ${l.qty} ${l.uom}`).join(", ") || "—" },
    { label: "Reference", render: (v) => v.reference || "—" },
    { label: "Amount ₹", right: true, render: (v) => <span className={v.cancelled ? "line-through text-slate-400" : ""}>{inr(v.amount)}</span> },
  ];

  return (
    <div className="space-y-6" data-testid="tally-data-page">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Integrations</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Tally Data</h1>
        <p className="text-sm text-slate-500 mt-1">Masters (customers, suppliers, stock, ledgers, groups, units, godowns …) and vouchers (sales invoices, receipts, purchases, payments, journals, notes …) pulled from Tally by the Tally Bridge Agent. Sending to Tally is set up in Settings → Tally Integration.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center"><Database className="w-5 h-5 text-blue-700" /></div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Pull from Tally</h3>
            <p className="text-xs text-slate-500">Tally's details win: a party's name, GSTIN, address, e-mail and phone overwrite the ERP's. ERP stock is never overwritten — Tally's closing stock is shown beside it.</p>
          </div>
          <Switch checked={cfg.enabled} disabled={!isAdmin} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="tally-sync-enabled" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div><Label>Parties & stock every (min)</Label><Input type="number" min={5} value={cfg.masters_interval_min} disabled={!isAdmin} onChange={(e) => setCfg({ ...cfg, masters_interval_min: Number(e.target.value) })} /></div>
          <div><Label>Vouchers every (min)</Label><Input type="number" min={1} value={cfg.vouchers_interval_min} disabled={!isAdmin} onChange={(e) => setCfg({ ...cfg, vouchers_interval_min: Number(e.target.value) })} /></div>
          <div><Label>Re-check last (days)</Label><Input type="number" min={1} value={cfg.recent_days} disabled={!isAdmin} onChange={(e) => setCfg({ ...cfg, recent_days: Number(e.target.value) })} /></div>
          <div><Label>Pull vouchers from</Label><Input type="date" value={cfg.pull_from} disabled={!isAdmin} onChange={(e) => setCfg({ ...cfg, pull_from: e.target.value })} /><div className="text-[11px] text-slate-500 mt-1">Empty = start of this financial year. Changing it re-pulls vouchers from the new date.</div></div>
          <div><Label>Receipt voucher types</Label><Input value={list(cfg.receipt_voucher_types)} disabled={!isAdmin} onChange={(e) => setCfg({ ...cfg, receipt_voucher_types: unlist(e.target.value) })} /></div>
          <div>
            <Label>Voucher types</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild disabled={!isAdmin}>
                <Button variant="outline" className="w-full justify-between font-normal" data-testid="tally-sync-pull-kinds">
                  <span className="truncate">
                    {PULL_OPTIONS.filter((o) => (cfg.pull_kinds || []).includes(o.value)).map((o) => o.label).join(", ") || "Nothing selected"}
                  </span>
                  <ChevronDown className="w-4 h-4 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {PULL_OPTIONS.map((o) => (
                  <DropdownMenuCheckboxItem
                    key={o.value}
                    checked={(cfg.pull_kinds || []).includes(o.value)}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(on) => setCfg({
                      ...cfg,
                      pull_kinds: on ? [...new Set([...(cfg.pull_kinds || []), o.value])] : (cfg.pull_kinds || []).filter((k) => k !== o.value),
                    })}
                  >{o.label}</DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="text-[11px] text-slate-500 mt-1">What to pull from Tally. Ledger = customers &amp; suppliers.</div>
          </div>
          <div><Label>Purchase Order voucher types</Label><Input value={list(cfg.purchase_order_voucher_types)} disabled={!isAdmin} onChange={(e) => setCfg({ ...cfg, purchase_order_voucher_types: unlist(e.target.value) })} /><div className="text-[11px] text-slate-500 mt-1">Comma-separated, exactly as named in Tally.</div></div>
        </div>
        <details className="mt-3">
          <summary className="text-sm text-slate-600 cursor-pointer">Other voucher type names</summary>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3">
            {VOUCHER_KINDS.filter((v) => !["receipt", "purchase_order"].includes(v.kind)).map((v) => (
              <div key={v.kind}>
                <Label>{v.typeLabel} voucher types</Label>
                <Input value={list(cfg[v.field])} disabled={!isAdmin} onChange={(e) => setCfg({ ...cfg, [v.field]: unlist(e.target.value) })} data-testid={`tally-sync-types-${v.kind}`} />
              </div>
            ))}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">Comma-separated, exactly as named in Tally (e.g. "Sales, GST Sales"). A Sales voucher numbered like an ERP Tax Invoice marks that invoice as already in Tally.</div>
        </details>
        {isAdmin && (
          <div className="flex flex-wrap gap-2 pt-4">
            <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="tally-sync-save">Save</Button>
            <Button variant="outline" onClick={() => post("/tally-sync/sync-now", "Sync starts on the agent's next check (within ~15 s)")}>Sync now</Button>
            <Button variant="outline" onClick={() => { if (window.confirm("Pull every voucher again from the pull-from date?")) post("/tally-sync/resync-vouchers", "Full voucher re-sync queued"); }}>Re-pull all vouchers</Button>
          </div>
        )}
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium text-slate-700">Recent pulls</div>
            <Button variant="ghost" size="sm" onClick={loadStatus}><RefreshCw className="w-4 h-4" /></Button>
          </div>
          <Table empty="Nothing pulled yet — enable the sync and make sure the agent is online (Settings → Tally Integration)." rows={runs} cols={[
            { label: "What", render: (r) => r.kind === "file_import" ? `File ${r.file_name || ""}` : r.kind === "vouchers" ? `${r.voucher_type} ${r.from} → ${r.to}` : r.kind === "ledgers" ? "Customers & suppliers" : r.kind === "account_ledgers" ? "Sales / GST ledgers" : r.kind === "masters" ? masterLabel(r.master_type) : r.kind === "invoice_number" ? `Invoice number${r.stats?.number ? ` ${r.stats.number}` : ""}` : "Stock items" },
            { label: "Status", render: (r) => <span className={r.status === "done" ? "text-emerald-700" : r.status === "failed" ? "text-red-600" : "text-amber-700"}>{r.status}{r.error ? ` — ${r.error}` : ""}</span> },
            { label: "Result", render: (r) => r.stats ? Object.entries(r.stats).filter(([k]) => k !== "unmatched").map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(" · ") : "—" },
            { label: "When", render: (r) => when(r.finished_at || r.requested_at) },
          ]} />
        </div>
      </div>

      {isAdmin && (
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="tally-import-file">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center"><FileUp className="w-5 h-5 text-blue-700" /></div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Import a Tally file</h3>
              <p className="text-xs text-slate-500">A file saved from Tally's Export — XML (masters, vouchers or Day Book) or Excel (Stock Items master). Applied like a pull: customers, suppliers, stock items and sales / GST ledgers update, vouchers and stock items are listed below. A Sales voucher numbered like an ERP Tax Invoice marks that invoice as already in Tally.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input type="file" accept=".xml,.xlsx,text/xml,application/xml,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="max-w-sm" onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImported(null); }} data-testid="tally-import-input" />
            <Button onClick={importXml} disabled={busy || !importFile} className="bg-blue-600 hover:bg-blue-700" data-testid="tally-import-button">Import</Button>
          </div>
          {imported && (
            <div className="mt-4 text-sm text-slate-700 space-y-1" data-testid="tally-import-result">
              {imported.company_mismatch && (
                <div className="text-amber-700">The file is from Tally company "{imported.company}", but Settings → Tally sends to "{imported.company_mismatch}". Pushes go to the company named in Settings.</div>
              )}
              <div>{Object.entries(imported).filter(([k]) => !["unmatched", "company", "company_mismatch", "names_from"].includes(k)).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(" · ")}</div>
              {imported.names_from && (
                <div className="text-amber-700">This Excel export has no Name column, so items are shown by their Mailing Name. ERP items are linked for HSN and Tally stock only; their Tally name for pushes is unchanged. Export again with the Name column to link them fully.</div>
              )}
              {(imported.unmatched || []).length > 0 && (
                <div className="text-xs text-slate-500">Stock items with no ERP item ({imported.items_unmatched}) are listed under Stock Items below.</div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap h-auto">
            {VOUCHER_KINDS.map((v) => <TabsTrigger key={v.kind} value={v.kind}>{v.label}</TabsTrigger>)}
            <TabsTrigger value="stock_items">Stock Items</TabsTrigger>
            <TabsTrigger value="masters">Masters</TabsTrigger>
          </TabsList>

          <TabsContent value="masters" className="space-y-3 pt-3">
            <div className="flex flex-wrap gap-2">
              {MASTER_TYPES.map((m) => (
                <Button key={m.value} size="sm" variant={masterType === m.value ? "default" : "outline"}
                  onClick={() => { setMasterType(m.value); loadTab("masters", m.value); }} data-testid={`tally-masters-${m.value}`}>
                  {m.label}{masters.counts?.[m.value] != null ? ` (${masters.counts[m.value]})` : ""}
                </Button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Input className="max-w-md" placeholder="Search name, group or GSTIN" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadTab("masters")} />
              <Button variant="outline" onClick={() => loadTab("masters")}>Search</Button>
              <span className="text-xs text-slate-500">{masters.items.length} of {masters.total} {masterLabel(masterType).toLowerCase()}</span>
            </div>
            <Table rows={masters.items} empty="No masters from Tally yet — turn on the All masters pull, or import a Masters export above." cols={[
              { label: "Name", render: (r) => r.name },
              { label: masterType === "voucher_type" ? "Base type" : "Under", render: (r) => r.parent || "Primary" },
              ...(masterType === "ledger" ? [
                { label: "GSTIN", render: (r) => r.gstin || "—" },
                { label: "Opening ₹", right: true, render: (r) => drcr(r.opening_balance) },
                { label: "Closing ₹", right: true, render: (r) => drcr(r.closing_balance) },
              ] : []),
              ...(masterType === "unit" ? [
                { label: "Formal name", render: (r) => r.formal_name || "—" },
                { label: "Decimals", right: true, render: (r) => r.decimals ?? "—" },
              ] : []),
              ...(masterType === "godown" ? [{ label: "Address", render: (r) => r.address || "—" }] : []),
              ...(masterType === "cost_centre" ? [{ label: "Category", render: (r) => r.category || "—" }] : []),
              { label: "Synced", render: (r) => when(r.synced_at) },
            ]} />
          </TabsContent>

          <TabsContent value="stock_items" className="space-y-3 pt-3">
            <div className="flex flex-wrap items-center gap-2">
              <Input className="max-w-md" placeholder="Search name, description, HSN, group or ERP code" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadTab("stock_items")} />
              <Button variant="outline" onClick={() => loadTab("stock_items")}>Search</Button>
              <span className="text-xs text-slate-500">{stock.items.length} of {stock.total} Tally stock items</span>
            </div>
            <Table rows={stock.items} empty="No stock items from Tally yet — import a Stock Items export above, or turn on the Stock pull." cols={[
              { label: "Name", render: (r) => r.name || <span className="text-slate-400">(no name in file)</span> },
              { label: "Description", render: (r) => r.description || "—" },
              { label: "Group / Category", render: (r) => [r.parent, r.category].filter(Boolean).join(" / ") || "—" },
              { label: "HSN", render: (r) => r.hsn || "—" },
              { label: "GST %", right: true, render: (r) => (r.gst_rate ?? "—") },
              { label: "Closing", right: true, render: (r) => (r.closing_qty == null ? "—" : `${r.closing_qty} ${r.uom || ""}`) },
              { label: "Part No.", render: (r) => <span className="mono text-xs">{(r.part_nos || []).join(", ") || "—"}</span> },
              { label: "ERP item", render: (r) => r.item_code ? <span className="text-emerald-700">{r.item_code}</span> : <span className="text-slate-400">Not linked</span> },
            ]} />
          </TabsContent>

          {VOUCHER_TABS.map((k) => (
            <TabsContent key={k} value={k} className="space-y-3 pt-3">
              <div className="flex gap-2 max-w-md">
                <Input placeholder="Search party, number or reference" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadTab(k)} />
                <Button variant="outline" onClick={() => loadTab(k)}>Search</Button>
              </div>
              <Table rows={vouchers} cols={voucherCols} empty="No vouchers pulled from Tally yet." />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
